/**
 * Transfer protocol over the DataChannel (or any injectable
 * `TransferChannel`—this lets the full protocol be unit tested without WebRTC,
 * with a pair of in-memory loopback channels).
 *
 * Messages (JSON in text):
 *  - `meta`  : encrypted envelope (AAD "meta") with { totalChunks, totalBytes, sha256 }.
 *  - `chunk` : index + encrypted envelope for the piece (AAD "chunk:<i>" —
 *              authenticates order; a reordered/reinjected chunk does not decrypt).
 *  - `done`  : end of chunks (content is already authenticated by meta+AAD).
 *  - `ack`   : receiver imported; includes inserted/skipped counts.
 *  - `abort` : cancellation with a reason.
 */

import { decryptEnvelope, decryptEnvelopeText, encryptEnvelope, sha256Base64 } from "./crypto";
import { utf8Decode, utf8Encode } from "./encoding";
import { isRecord } from "./guards";

/** Plaintext chunk; the encrypted envelope grows ~37% in base64 and must stay
 * below the DataChannel's interoperable message limit (~16 KiB). */
export const CHUNK_BYTES = 8 * 1024;
/** Hard limit for the serialized snapshot (before encryption). */
export const MAX_SNAPSHOT_BYTES = 16 * 1024 * 1024;
/** Pauses writing when the channel buffer exceeds this threshold. */
export const CHANNEL_BACKPRESSURE_BYTES = 1 * 1024 * 1024;

export interface TransferMeta {
  totalChunks: number;
  totalBytes: number;
  sha256: string;
}

export type TransferWireMessage =
  | { kind: "meta"; envelope: string }
  | { kind: "chunk"; index: number; envelope: string }
  | { kind: "done" }
  | { kind: "ack"; inserted: number; skipped: number }
  | { kind: "abort"; reason: string };

/** Minimal common surface for RTCDataChannel and a test loopback channel. */
export interface TransferChannel {
  send(data: string): void;
  readonly bufferedAmount: number;
  onMessage(handler: (data: string) => void): void;
  onClose(handler: () => void): void;
}

export function encodeWireMessage(message: TransferWireMessage): string {
  return JSON.stringify(message);
}

export function decodeWireMessage(data: string): TransferWireMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) {
    return null;
  }
  const msg = parsed;
  switch (msg.kind) {
    case "meta": {
      return typeof msg.envelope === "string" ? { kind: "meta", envelope: msg.envelope } : null;
    }
    case "chunk": {
      return typeof msg.index === "number" && typeof msg.envelope === "string"
        ? { kind: "chunk", index: msg.index, envelope: msg.envelope }
        : null;
    }
    case "done": {
      return { kind: "done" };
    }
    case "ack": {
      return typeof msg.inserted === "number" && typeof msg.skipped === "number"
        ? { kind: "ack", inserted: msg.inserted, skipped: msg.skipped }
        : null;
    }
    case "abort": {
      return typeof msg.reason === "string" ? { kind: "abort", reason: msg.reason } : null;
    }
    default: {
      return null;
    }
  }
}

export function splitIntoChunks(bytes: Uint8Array, chunkBytes: number = CHUNK_BYTES): Uint8Array[] {
  if (chunkBytes <= 0) {
    throw new Error("chunkBytes must be positive");
  }
  if (bytes.length === 0) {
    return [];
  }
  const chunks: Uint8Array[] = [];
  for (let offset = 0; offset < bytes.length; offset += chunkBytes) {
    chunks.push(bytes.subarray(offset, offset + chunkBytes));
  }
  return chunks;
}

export function joinChunks(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, c) => sum + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

function waitForDrain(channel: TransferChannel): Promise<void> {
  return new Promise((resolve) => {
    const poll = () => {
      if (channel.bufferedAmount <= CHANNEL_BACKPRESSURE_BYTES) {
        resolve();
      } else {
        setTimeout(poll, 50);
      }
    };
    poll();
  });
}

export interface SendSnapshotOptions {
  onProgress?: (sentBytes: number, totalBytes: number) => void;
}

/**
 * SENDER: serializes, encrypts by chunks, and sends through the channel. Returns
 * a promise that resolves with the receiver's `ack` (or rejects on `abort`).
 */
export async function sendPayloadOverChannel(
  channel: TransferChannel,
  key: CryptoKey,
  payloadJson: string,
  options: SendSnapshotOptions = {},
): Promise<{ inserted: number; skipped: number }> {
  const bytes = utf8Encode(payloadJson);
  if (bytes.length > MAX_SNAPSHOT_BYTES) {
    const abort: TransferWireMessage = { kind: "abort", reason: "too-large" };
    channel.send(encodeWireMessage(abort));
    throw new TransferProtocolError("too-large");
  }

  const ackPromise = new Promise<{ inserted: number; skipped: number }>((resolve, reject) => {
    channel.onMessage((data) => {
      const msg = decodeWireMessage(data);
      if (msg?.kind === "ack") {
        resolve({ inserted: msg.inserted, skipped: msg.skipped });
      } else if (msg?.kind === "abort") {
        reject(new TransferProtocolError(msg.reason));
      }
    });
    channel.onClose(() => {
      reject(new TransferProtocolError("channel-closed"));
    });
  });
  // The receiver may send `abort` (e.g. wrong key → decrypt-failed) or the
  // channel may close WHILE we are still encrypting/sending chunks — that is,
  // BEFORE this function returns `ackPromise` (or if an intermediate `await`
  // throws and we never reach the `return`). Without a handler already attached,
  // the runtime marks that early rejection as an "unhandled rejection" (intermittent
  // red in CI under load). A no-op `.catch` silences that detection; the REAL
  // rejection still propagates through the `ackPromise` we return (the caller awaits it).
  ackPromise.catch(() => undefined);

  const chunks = splitIntoChunks(bytes);
  const meta: TransferMeta = {
    totalChunks: chunks.length,
    totalBytes: bytes.length,
    sha256: await sha256Base64(bytes),
  };
  channel.send(
    encodeWireMessage({
      kind: "meta",
      envelope: await encryptEnvelope(key, JSON.stringify(meta), "meta"),
    }),
  );

  let sent = 0;
  for (let i = 0; i < chunks.length; i++) {
    if (channel.bufferedAmount > CHANNEL_BACKPRESSURE_BYTES) {
      await waitForDrain(channel);
    }
    channel.send(
      encodeWireMessage({
        kind: "chunk",
        index: i,
        envelope: await encryptEnvelope(key, chunks[i]!, `chunk:${i}`),
      }),
    );
    sent += chunks[i]!.length;
    options.onProgress?.(sent, bytes.length);
  }
  channel.send(encodeWireMessage({ kind: "done" }));

  return ackPromise;
}

export class TransferProtocolError extends Error {
  constructor(public readonly reason: string) {
    super(`P2P transfer failed: ${reason}`);
    this.name = "TransferProtocolError";
  }
}

export interface ReceivePayloadOptions {
  onProgress?: (receivedBytes: number, totalBytes: number) => void;
  /**
   * Imports the decrypted payload and returns the counts for the `ack`.
   * Runs BEFORE sending the ack: if import fails, the sender receives `abort`
   * and does not mark the transfer as complete.
   */
  importPayload: (payloadJson: string) => Promise<{ inserted: number; skipped: number }>;
}

/**
 * RECEIVER: accumulates chunks, verifies integrity (SHA-256 + per-index AAD),
 * decrypts, imports, and responds with `ack`.
 */
export function receivePayloadOverChannel(
  channel: TransferChannel,
  key: CryptoKey,
  options: ReceivePayloadOptions,
): Promise<{ inserted: number; skipped: number }> {
  return new Promise((resolve, reject) => {
    let meta: TransferMeta | null = null;
    const chunks = new Map<number, Uint8Array>();
    let receivedBytes = 0;
    let settled = false;

    const fail = (reason: string) => {
      if (settled) {
        return;
      }
      settled = true;
      try {
        channel.send(encodeWireMessage({ kind: "abort", reason }));
      } catch {
        // The channel may already be closed
      }
      reject(new TransferProtocolError(reason));
    };

    const finish = async () => {
      if (settled) {
        return;
      }
      if (!meta) {
        fail("missing-meta");
        return;
      }
      if (chunks.size !== meta.totalChunks) {
        fail("missing-chunks");
        return;
      }
      const ordered: Uint8Array[] = [];
      for (let i = 0; i < meta.totalChunks; i++) {
        const chunk = chunks.get(i);
        if (!chunk) {
          fail("missing-chunks");
          return;
        }
        ordered.push(chunk);
      }
      const bytes = joinChunks(ordered);
      if (bytes.length !== meta.totalBytes || (await sha256Base64(bytes)) !== meta.sha256) {
        fail("integrity-check-failed");
        return;
      }
      try {
        const counts = await options.importPayload(utf8Decode(bytes));
        settled = true;
        channel.send(encodeWireMessage({ kind: "ack", ...counts }));
        resolve(counts);
      } catch {
        fail("import-failed");
      }
    };

    // Handlers are async (decryption): a single-pump queue processes messages
    // IN ORDER—without this, a short `done` can get ahead of the `meta` still
    // decrypting and fail with `missing-meta`.
    const inbox: string[] = [];
    let pumping = false;
    const handleMessage = async (data: string) => {
      const msg = decodeWireMessage(data);
      if (!msg || settled) {
        return;
      }
      try {
        switch (msg.kind) {
          case "meta": {
            const decoded: unknown = JSON.parse(
              await decryptEnvelopeText(key, msg.envelope, "meta"),
            );
            if (
              !isRecord(decoded) ||
              typeof decoded.totalChunks !== "number" ||
              decoded.totalChunks < 0 ||
              typeof decoded.totalBytes !== "number" ||
              decoded.totalBytes > MAX_SNAPSHOT_BYTES ||
              typeof decoded.sha256 !== "string"
            ) {
              fail("invalid-meta");
              return;
            }
            meta = {
              totalChunks: decoded.totalChunks,
              totalBytes: decoded.totalBytes,
              sha256: decoded.sha256,
            };
            break;
          }
          case "chunk": {
            const plain = await decryptEnvelope(key, msg.envelope, `chunk:${msg.index}`);
            if (!chunks.has(msg.index)) {
              receivedBytes += plain.length;
            }
            chunks.set(msg.index, plain);
            if (receivedBytes > MAX_SNAPSHOT_BYTES) {
              fail("too-large");
              return;
            }
            if (meta) {
              options.onProgress?.(receivedBytes, meta.totalBytes);
            }
            break;
          }
          case "done": {
            await finish();
            break;
          }
          case "abort": {
            fail(msg.reason);
            break;
          }
          case "ack": {
            break;
          } // only the sender consumes it
        }
      } catch {
        // Invalid envelope / incorrect key / tampered AAD
        fail("decrypt-failed");
      }
    };
    const pump = async () => {
      if (pumping) {
        return;
      }
      pumping = true;
      let next = inbox.shift();
      while (next !== undefined) {
        await handleMessage(next);
        next = inbox.shift();
      }
      pumping = false;
    };
    channel.onMessage((data) => {
      inbox.push(data);
      void pump();
    });
    channel.onClose(() => {
      if (!settled) {
        settled = true;
        reject(new TransferProtocolError("channel-closed"));
      }
    });
  });
}
